import {
  Button,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import { Box } from '@mui/system'
import experienceCriteriaIcon from 'assets/svgs/icon_course_experience_criteria.svg'
import actionIcon from 'assets/svgs/icon_cs_action.svg'
import completedIcon from 'assets/svgs/icon_ls_completed.svg'
import downloadIcon from 'assets/svgs/icon_download.svg'
import { FC, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  useGetExperienceCriteriaQuery,
  useSubmitExperienceCriteriaCommandMutation,
} from 'store/api'
import { useAppSelector } from 'store'
import { selectUserIsImpersonate } from 'store/slices'
import ModuleCard from 'components/EVLevel/ModulesList/ModuleCard'
import {
  StyledBoxWrapper,
  StyledButton,
  StyledTable,
} from 'components/EVLevel/EVDetail/ExperienceCriteria/styles'
import { ITrainingLevelDetails } from 'interfaces'

interface ExperienceCriteriaProps {
  hasExperienceCriteria: boolean
  levelUUID: string
  staffMemberUUID?: string
  experienceCriteria?: ITrainingLevelDetails['experienceCriteria']
  experienceCriteriaMonths?: string
}

const ExperienceCriteria: FC<ExperienceCriteriaProps> = ({
  hasExperienceCriteria,
  levelUUID,
  staffMemberUUID,
  experienceCriteria,
  experienceCriteriaMonths,
}) => {
  const isImpersonate = useAppSelector(selectUserIsImpersonate)

  const [expanded, setExpanded] = useState(false)
  const [checked, setChecked] = useState(false)
  const { palette } = useTheme()
  const { data: experiencCriteriaQueryTypes } = useGetExperienceCriteriaQuery('')

  const [submitExperienceCriteria, { isLoading }] =
    useSubmitExperienceCriteriaCommandMutation()

  const onFormSubmit = async () => {
    const formData = new FormData()
    formData.append('staffMemberUUID', staffMemberUUID as string)
    formData.append('levelUUID', levelUUID)
    submitExperienceCriteria(formData)
  }

  const handleDownloadButton = () => {
    const docData = experienceCriteria?.link
    if (docData) {
      if (/(iPad|iPhone|iPod)/g.test(navigator.userAgent)) {
        const url = docData
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url)
        xhr.responseType = 'blob'
        xhr.onload = function () {
          const blob = xhr.response
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.click()
        }
        xhr.send()
      } else {
        window.open(docData, '_blank')?.focus()
      }
    }
  }

  if (!experiencCriteriaQueryTypes) {
    return null
  }

  return (
    <ModuleCard
      open={expanded}
      onToggleOpen={() => setExpanded(!expanded)}
      header={
        <Box
          display="grid"
          gridTemplateColumns="auto 1fr auto"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Box
            width="70px"
            height="43px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              img: {
                width: '44px',
                height: '43px',
              },
            }}
          >
            <img src={experienceCriteriaIcon} alt="Experience Criteria" />
          </Box>
          <Typography
            align="left"
            ml={2}
            color={palette.grey[700]}
            textTransform="uppercase"
          >
            <FormattedMessage id="evLevel.EVDetail.experienceCriteria.title" />
          </Typography>
          <img
            src={hasExperienceCriteria ? completedIcon : actionIcon}
            alt={hasExperienceCriteria ? 'completed icon' : 'warning icon'}
            style={{ width: '40px', height: '40px' }}
          />
        </Box>
      }
    >
      <StyledBoxWrapper>
        <Typography color={palette.grey[700]} fontSize={14} marginBottom={2}>
          <FormattedMessage
            id="evLevel.EVDetail.experienceCriteria.subtitle1"
            values={{ numberMonths: experienceCriteriaMonths }}
          />
        </Typography>
        <Typography color={palette.grey[700]} fontSize={14} marginBottom={2}>
          <FormattedMessage
            id="evLevel.EVDetail.experienceCriteria.subtitle2"
            values={{ numberMonths: experienceCriteriaMonths }}
          />
        </Typography>
        <Typography color={palette.grey[700]} fontSize={14}>
          <FormattedMessage
            id="evLevel.EVDetail.experienceCriteria.subtitle3"
            values={{ numberMonths: experienceCriteriaMonths }}
          />
        </Typography>
        <List sx={{ listStyleType: 'disc', pl: 4 }}>
          <ListItem
            disableGutters
            sx={{
              display: 'list-item',
              px: 0,
              py: 0.5,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <Typography color={palette.grey[700]} fontSize={14}>
              <FormattedMessage
                id="evLevel.EVDetail.experienceCriteria.subtitle4"
                values={{ numberMonths: experienceCriteriaMonths }}
              />
            </Typography>
          </ListItem>
          <ListItem
            disableGutters
            sx={{
              display: 'list-item',
              px: 0,
              py: 0.5,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <Typography color={palette.grey[700]} fontSize={14}>
              <FormattedMessage
                id="evLevel.EVDetail.experienceCriteria.subtitle5"
                values={{ numberMonths: experienceCriteriaMonths }}
              />
            </Typography>
          </ListItem>
          <ListItem
            disableGutters
            sx={{
              display: 'list-item',
              px: 0,
              py: 0.5,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <Typography color={palette.grey[700]} fontSize={14}>
              <FormattedMessage
                id="evLevel.EVDetail.experienceCriteria.subtitle6"
                values={{ numberMonths: experienceCriteriaMonths }}
              />
            </Typography>
          </ListItem>
          <ListItem
            disableGutters
            sx={{
              display: 'list-item',
              px: 0,
              py: 0.5,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <Typography color={palette.grey[700]} fontSize={14}>
              <FormattedMessage
                id="evLevel.EVDetail.experienceCriteria.subtitle7"
                values={{ numberMonths: experienceCriteriaMonths }}
              />
            </Typography>
          </ListItem>
          <ListItem
            disableGutters
            sx={{
              display: 'list-item',
              px: 0,
              py: 0.5,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <Typography color={palette.grey[700]} fontSize={14}>
              <FormattedMessage
                id="evLevel.EVDetail.experienceCriteria.subtitle8"
                values={{ numberMonths: experienceCriteriaMonths }}
              />
            </Typography>
          </ListItem>
        </List>

        {experienceCriteria?.link ? (
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell width="40%">
                  <Typography>
                    <FormattedMessage id="evLevel.EVDetail.experienceCriteria.table.fileName" />
                  </Typography>
                </TableCell>
                <TableCell width="40%">
                  <Typography>
                    <FormattedMessage id="evLevel.EVDetail.experienceCriteria.table.fileType" />
                  </Typography>
                </TableCell>
                <TableCell width="20%" align="right">
                  <Typography>
                    <FormattedMessage id="app.buttons.download" />
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography>{experienceCriteria?.filename}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{experienceCriteria?.filetype}</Typography>
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <Button
                    onClick={() => {
                      handleDownloadButton()
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} color="primary" />
                    ) : (
                      <img src={downloadIcon} width="20" />
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </StyledTable>
        ) : null}

        {!hasExperienceCriteria ? (
          <form
            onSubmit={e => {
              e.preventDefault()
              onFormSubmit()
            }}
            encType="multipart/form-data"
          >
            <Box display="flex" alignItems="center" marginLeft="-12px" marginBottom={1}>
              <Checkbox
                id="confimation"
                checked={checked}
                onChange={() => setChecked(!checked)}
              />
              <Typography
                component="label"
                htmlFor="confimation"
                fontSize={14}
                variant="body2"
                color={palette.grey[700]}
              >
                <FormattedMessage id="evLevel.EVDetail.experienceCriteria.checkbox.label" />
              </Typography>
            </Box>

            <StyledButton type="submit" disabled={isImpersonate || !checked || isLoading}>
              {isLoading ? (
                <CircularProgress size={20} color="primary" />
              ) : (
                <FormattedMessage id="evLevel.EVDetail.experienceCriteria.button.text" />
              )}
            </StyledButton>
          </form>
        ) : null}
      </StyledBoxWrapper>
    </ModuleCard>
  )
}

export default ExperienceCriteria
